import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { money, date } from '../lib/format';

const KINDS = [
  { value: 'part', label: 'Part' },
  { value: 'license', label: 'License' },
  { value: 'service', label: 'Service' },
  { value: 'other', label: 'Other' },
];
const emptyItem = () => ({ kind: 'part', description: '', vendor: '', quantity: 1, unit_price: '' });

export default function Approvals() {
  const { activeId, activeBusiness } = useBusiness();
  const { can } = useAuth();
  const editable = can('expenses.manage');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('new');
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(null);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);

  const loadList = useCallback(() => {
    setLoading(true);
    api(`/approvals?business_id=${activeId}`)
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [activeId]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    api(`/customers?business_id=${activeId}`).then((d) => setCustomers((Array.isArray(d) ? d : []).filter((c) => c.is_active !== false))).catch(() => setCustomers([]));
  }, [activeId]);

  const total = useMemo(() => items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0), [items]);

  function openNew() {
    setMode('new'); setCurrent(null);
    setForm({ customer_id: '', title: '', notes: '' });
    setItems([emptyItem()]);
    setFormError(null); setOpen(true);
  }
  async function openRow(row) {
    setFormError(null); setOpen(true);
    try {
      const a = await api(`/approvals/${row.id}`);
      setCurrent(a); setMode('edit');
      setForm({ customer_id: a.customer_id, title: a.title, notes: a.notes || '' });
      setItems((a.items || []).map((it) => ({ kind: it.kind, description: it.description, vendor: it.vendor || '', quantity: Number(it.quantity), unit_price: Number(it.unit_price) })));
    } catch { setOpen(false); }
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setItems((xs) => xs.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((xs) => [...xs, emptyItem()]);
  const removeItem = (i) => setItems((xs) => (xs.length > 1 ? xs.filter((_, idx) => idx !== i) : xs));

  const isDraft = !current || current.status === 'draft';

  async function save() {
    setSaving(true); setFormError(null);
    try {
      const body = { business_id: activeId, customer_id: form.customer_id, title: form.title, notes: form.notes, items };
      const saved = mode === 'edit' && current ? await api(`/approvals/${current.id}`, { method: 'PUT', body }) : await api('/approvals', { method: 'POST', body });
      setCurrent(saved); setMode('edit'); loadList();
    } catch (e) { setFormError(e?.detail || 'Could not save.'); }
    finally { setSaving(false); }
  }

  async function action(path, label) {
    setBusy(true); setFormError(null);
    try {
      await api(`/approvals/${current.id}/${path}`, { method: 'POST' });
      const fresh = await api(`/approvals/${current.id}`);
      setCurrent(fresh);
      loadList();
    } catch (e) { setFormError(e?.detail || `Could not ${label}.`); }
    finally { setBusy(false); }
  }

  const columns = [
    { key: 'title', header: 'Request' },
    { key: 'customer_name', header: 'Customer', render: (r) => r.customer_name || '—' },
    { key: 'item_count', header: 'Items', render: (r) => r.item_count },
    { key: 'subtotal', header: 'Total', align: 'right', render: (r) => <span className="num">{money(r.subtotal)}</span> },
    { key: 'created_at', header: 'Created', render: (r) => date(r.created_at) },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  const title = mode === 'new' ? 'New approval request' : (current?.title || 'Approval request');
  const footer = (
    <>
      <button className="btn" onClick={() => !saving && setOpen(false)}>Close</button>
      {editable && isDraft && <button className="btn btn--primary" onClick={save} disabled={saving || !form?.customer_id || !form?.title?.trim()}>{saving ? 'Saving…' : 'Save draft'}</button>}
      {editable && current && ['draft', 'pending'].includes(current.status) && (
        <button className="btn btn--primary" onClick={() => action('send', 'send')} disabled={busy}>{busy ? 'Working…' : (current.status === 'pending' ? 'Re-send' : 'Send for approval')}</button>
      )}
      {editable && current?.status === 'approved' && <button className="btn btn--primary" onClick={() => action('order', 'mark ordered')} disabled={busy}>Mark ordered</button>}
    </>
  );

  return (
    <>
      <PageHeader title="Purchase approvals" subtitle={activeBusiness?.name}
        actions={editable && <button className="btn btn--primary" onClick={openNew}>New request</button>} />

      <section className="panel">
        <DataTable columns={columns} rows={rows} loading={loading} onRowClick={openRow} emptyMessage="No approval requests yet." />
      </section>

      <Drawer open={open} title={title} width={720} onClose={() => !saving && setOpen(false)} footer={form && footer}>
        {form && (
          <>
            {current && (
              <div className="appr-status">
                <StatusBadge status={current.status} />
                {current.status === 'pending' && current.sent_at && <span className="hint">Sent {date(current.sent_at)} — awaiting customer</span>}
                {current.status === 'approved' && <span className="hint">Approved by {current.decided_by_name} on {date(current.decided_at)}</span>}
                {current.status === 'declined' && <span className="hint">Declined by {current.decided_by_name} on {date(current.decided_at)}</span>}
                {current.status === 'ordered' && <span className="hint">Ordered {date(current.ordered_at)}</span>}
              </div>
            )}
            {current?.decision_comments && <div className="appr-comment">“{current.decision_comments}”</div>}

            <div className="form-grid">
              <div className="field"><label>Customer</label>
                <select value={form.customer_id} onChange={(e) => setField('customer_id', e.target.value)} disabled={!isDraft}>
                  <option value="">Select…</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="field"><label>Title</label>
                <input value={form.title} onChange={(e) => setField('title', e.target.value)} placeholder="e.g. Replacement servo + annual license" disabled={!isDraft} /></div>
            </div>
            <div className="field"><label>Notes for the customer (optional)</label>
              <textarea rows={2} value={form.notes} onChange={(e) => setField('notes', e.target.value)} disabled={!isDraft} /></div>

            <h3 className="form-section">Items to approve</h3>
            <div className="appr-items">
              {items.map((it, i) => (
                <div className="appr-item" key={i}>
                  <select value={it.kind} onChange={(e) => setItem(i, 'kind', e.target.value)} disabled={!isDraft}>
                    {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                  </select>
                  <input className="appr-item__desc" placeholder="Description" value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} disabled={!isDraft} />
                  <input className="appr-item__vendor" placeholder="Vendor / SKU" value={it.vendor} onChange={(e) => setItem(i, 'vendor', e.target.value)} disabled={!isDraft} />
                  <input className="appr-item__qty" type="number" min="0" step="any" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} disabled={!isDraft} />
                  <input className="appr-item__price" type="number" min="0" step="0.01" placeholder="Unit $" value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value)} disabled={!isDraft} />
                  <span className="appr-item__amt num">{money((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</span>
                  {isDraft && <button className="btn btn--sm btn--ghost" onClick={() => removeItem(i)} disabled={items.length === 1}>×</button>}
                </div>
              ))}
            </div>
            {isDraft && <button className="btn btn--sm" onClick={addItem} style={{ marginTop: 8 }}>Add item</button>}

            <div className="appr-total"><span>Total</span><span className="num">{money(total)}</span></div>

            {formError && <div className="form-error" style={{ marginTop: 10 }}>{formError}</div>}

            {editable && current && ['draft', 'pending', 'declined'].includes(current.status) && (
              <div className="quote-actions" style={{ marginTop: 16 }}>
                <button className="btn btn--sm btn--danger" onClick={() => action('cancel', 'cancel')} disabled={busy}>Cancel request</button>
              </div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
